import { sample } from 'lodash';
import ordinal from 'ordinal';

import { DOMAIN_CALL_ENDED, DOMAIN_CALL_STARTED } from '../../actions/actionTypes/domain';
import { INIT } from '../../actions/actionTypes/trainer';
import promptsEn from '../config/prompts-en.json';
import promptsDe from '../config/prompts-de.json';
import { USER_PROFILE_FETCH_SUCCESS } from '../../actions/actionTypes/userProfile';
import type { PromptCurrentPrompt } from '../../types';

const now = new Date();
const currentDay = now.getDate();

interface PromptConfigItem {
    expirationDate: number | null;
}

interface PromptItem extends PromptCurrentPrompt {
    category?: string;
    group?: string;
}

interface PromptState {
    currentPrompt: Partial<PromptItem>;
    config: {
        morning: PromptConfigItem;
        afternoon: PromptConfigItem;
    };
    calls: {
        expirationDate: number | null;
        count: number;
    };
    initialized: boolean;
    languageCode: string;
}

export const initialState: PromptState = {
    currentPrompt: {},
    config: {
        morning: { expirationDate: null },
        afternoon: { expirationDate: null }
    },
    calls: {
        expirationDate: null,
        count: 0
    },
    initialized: false,
    languageCode: 'de-DE'
};

const getPromptCategory = ({ state, isCallEnded }: { state: PromptState; isCallEnded?: boolean }) => {
    if (isCallEnded) {
        return 'after-the-x-call';
    }
    if (new Date(now.getFullYear(), now.getMonth(), currentDay, 12, 0, 0) > new Date()) {
        const morningExpiration = state.config?.morning?.expirationDate;
        if (!morningExpiration || new Date(morningExpiration) < new Date()) {
            return 'morning';
        }
    }

    if (new Date(now.getFullYear(), now.getMonth(), currentDay, 12, 0, 0) < new Date()) {
        const afternoonExpiration = state.config?.afternoon?.expirationDate;
        if (!afternoonExpiration || new Date(afternoonExpiration) < new Date()) {
            return 'afternoon';
        }
    }

    return 'generic';
};

const getExpirationTime = (category: string): Date | null => {
    switch (category) {
        case 'morning':
            return new Date(now.getFullYear(), now.getMonth(), currentDay + 1, 8, 0, 0);
        case 'afternoon':
            return new Date(now.getFullYear(), now.getMonth(), currentDay + 1, 12, 0, 0);
        default:
            return null;
    }
};

const getOrdinal = (number: number, languageCode: string): string | number => {
    if (languageCode === 'de-DE') {
        if (number === 1) {
            return 'einen';
        }
        return number;
    }

    return ordinal(number || 0);
};

function isPromptItem(item: unknown): item is PromptItem {
    return typeof item === 'object' && item !== null;
}

const toPromptItems = (items: unknown): PromptItem[] => (Array.isArray(items) ? items.filter(isPromptItem) : []);

const getRandomPrompt = (category: string, completedCalls = 0, languageCode = 'de-DE') => {
    const prompts = languageCode === 'de-DE' ? toPromptItems(promptsDe) : toPromptItems(promptsEn);

    const categoryPrompts = prompts.filter(item => item.category === category);
    const random = sample(categoryPrompts) || {};

    function getText(word: string) {
        const calls = () => {
            if (languageCode === 'de-DE') {
                return completedCalls === 1 ? 'Anruf' : 'Anrufe';
            }
            return completedCalls === 1 ? 'call' : 'calls';
        };

        const sentence = word
            .replace('#ordinal#', String(getOrdinal(completedCalls, languageCode)))
            .replace('#numberOfCalls#', String(completedCalls))
            .replace('#calls#', calls());

        // uppercase first letter
        return sentence.charAt(0).toUpperCase() + sentence.slice(1);
    }

    if (category === 'after-the-x-call' || category === 'morning' || category === 'afternoon') {
        const random2 = sample(categoryPrompts.filter(item => item.group === random.group)) || {};

        return {
            ...random,
            title: getText(String(random.title || '')),
            subtext: getText(String(random2.subtext || ''))
        };
    }

    random.title = getText(String(random.title || ''));
    random.subtext = getText(String(random.subtext || ''));

    return random;
};

function isTimedCategory(category: string): category is 'morning' | 'afternoon' {
    return category === 'morning' || category === 'afternoon';
}

const producePrompt = (state: PromptState, completedCalls = 0, isCallEnded = false) => {
    const category = getPromptCategory({ state, isCallEnded });
    const expirationDate = getExpirationTime(category);

    if (isTimedCategory(category) && new Date(state.config[category].expirationDate || 0) > new Date()) {
        const prompt = getRandomPrompt(category, completedCalls, state.languageCode);
        return {
            ...state,
            currentPrompt: prompt,
            config: {
                ...state.config,
                [category]: { expirationDate: expirationDate ? expirationDate.getTime() : null }
            }
        };
    }

    const prompt = getRandomPrompt(category, completedCalls, state.languageCode);

    return {
        currentPrompt: prompt,
        config: {
            ...state.config,
            [category]: {
                expirationDate: expirationDate ? expirationDate.getTime() : null
            }
        }
    };
};

interface ReducerAction {
    type: string;
    payload?: ReducerPayload;
}

interface ReducerPayload {
    languageCode?: string;
}

function getLanguageCode(payload: ReducerPayload | undefined): string | undefined {
    return typeof payload?.languageCode === 'string' ? payload.languageCode : undefined;
}

const promptReducer = (state: PromptState | undefined, action: ReducerAction): PromptState => {
    const currentState = state ?? initialState;
    const { type } = action;

    switch (type) {
        case DOMAIN_CALL_STARTED:
            return {
                ...currentState,
                currentPrompt: {}
            };
        case DOMAIN_CALL_ENDED: {
            let completedCalls = currentState.calls.count + 1;

            if (currentState.calls.expirationDate !== new Date().getDate()) {
                completedCalls = 1;
            }

            return {
                ...currentState,
                ...producePrompt(currentState, completedCalls, true),
                calls: {
                    expirationDate: new Date().getDate(),
                    count: completedCalls
                }
            };
        }

        case INIT: {
            if (!currentState.initialized) {
                return {
                    ...currentState,
                    ...producePrompt(currentState)
                };
            }
            return currentState;
        }

        case USER_PROFILE_FETCH_SUCCESS: {
            return {
                ...currentState,
                languageCode: getLanguageCode(action.payload) || currentState.languageCode
            };
        }

        default:
            return currentState;
    }
};

export default promptReducer;
